import React from 'react';

import { SettingSection } from '@lumapps/designer-widget-settings/components/SettingSection';
import { PdfPickerDialog } from '@lumapps/file-pickers/components/PdfPickerDialog';
import { UploadFileControlled } from '@lumapps/lumx-files/components/UploadFile/UploadFileControlled';
import { EmptyState } from '@lumapps/lumx-states/components/EmptyState';
import { mdiFilePdf, mdiPlusBox } from '@lumapps/lumx/icons';
import { Alignment, ColorPalette, Emphasis, FlexBox, ProgressCircular, Size } from '@lumapps/lumx/react';
import { useGetDocument } from '@lumapps/medias-document/hooks/useGetDocument';
import type { DocumentV1 } from '@lumapps/medias-document/types';
import { getMediaContent } from '@lumapps/medias/utils';
import { isDocPath } from '@lumapps/medias/utils/isDocPath';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, isTranslateObject, type TranslateObject, useTranslate } from '@lumapps/translations';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { isURLValid } from '@lumapps/utils/string/isUrl';

import { RELATIVE_PATH_REGEX } from '../../constants';
import { WIDGET_PDF_PREVIEW } from '../../keys';
import type { WidgetPdfPreviewSettingsProps } from '../../type';

/**
 * Settings component for the PDF preview widget.
 * @param {WidgetPdfPreviewSettingsProps}
 * @returns WidgetPdfPreviewSettings
 * */
export const WidgetPdfPreviewSettings: React.FC<WidgetPdfPreviewSettingsProps> = ({ properties, onChange }) => {
    const { translateKey, translateObject } = useTranslate();
    const currentInputLang = useSelector(inputLanguageSelector);

    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedFile, setSelectedFile] = React.useState<File | undefined>(undefined);
    const [linkFile, setLinkFile] = React.useState<string>();

    // Resolve the stored URL/path for the current language
    const rawValue = React.useMemo(() => {
        const { pdfUrl } = properties || {};
        if (!pdfUrl) {
            return undefined;
        }
        if (typeof pdfUrl === 'object' && isTranslateObject(pdfUrl)) {
            return pdfUrl[currentInputLang];
        }
        return pdfUrl as string;
    }, [properties, currentInputLang]);

    // A docPath must be fetched; a direct URL or relative path can be used as-is
    const isDirectUrl = React.useMemo(() => {
        if (!rawValue) {
            return false;
        }
        return isURLValid(rawValue) || RELATIVE_PATH_REGEX.test(rawValue);
    }, [rawValue]);

    const shouldFetchDocument = Boolean(rawValue) && !isDirectUrl && isDocPath(rawValue);

    const { data: currentDocument, isLoading } = useGetDocument(
        { docPath: rawValue || '' },
        { enabled: shouldFetchDocument },
    );

    const isActuallyLoading = isLoading && shouldFetchDocument;

    // Shared base: per-language translations already stored in pdfUrl
    const existingTranslations = React.useMemo((): TranslateObject<string> => {
        if (properties?.pdfUrl && typeof properties.pdfUrl === 'object' && isTranslateObject(properties.pdfUrl)) {
            return properties.pdfUrl;
        }
        return {};
    }, [properties]);

    // Initialize display state from the already-saved document
    React.useEffect(() => {
        if (currentDocument) {
            const mediaContent = getMediaContent(currentDocument, translateObject, currentInputLang);
            setSelectedFile(new File([], mediaContent.name || '', { type: mediaContent.type }));
            setLinkFile(mediaContent.servingUrl);
        }
        // Reset display state when no PDF is configured for the current language
        if (!rawValue) {
            setSelectedFile(undefined);
            setLinkFile(undefined);
        } else if (isDirectUrl && rawValue) {
            // Path like rawValue = /serve/2995952448771099-3377682181695705/file-sample_150kB.pdf — no fetch needed, use it directly
            const filename = rawValue.split('/').pop() || '';
            setSelectedFile(new File([], filename, { type: 'application/pdf' }));
            setLinkFile(rawValue);
        }
    }, [currentDocument, isDirectUrl, rawValue, translateObject, currentInputLang]);

    const onConfirmHandler = React.useCallback(
        (value: DocumentV1 | undefined) => {
            if (!value) {
                setIsOpen(false);
                return;
            }
            onChange({ ...properties, pdfUrl: { ...existingTranslations, [currentInputLang]: value.docPath } });

            const mediaContent = getMediaContent(value, translateObject, currentInputLang);
            setSelectedFile(new File([], mediaContent.name || '', { type: mediaContent.type }));
            setLinkFile(mediaContent.servingUrl);
            setIsOpen(false);
        },
        [currentInputLang, existingTranslations, onChange, properties, translateObject],
    );

    const onDelete = React.useCallback(() => {
        setSelectedFile(undefined);
        setLinkFile(undefined);
        const updatedTranslations = Object.fromEntries(
            Object.entries(existingTranslations).filter(([lang]) => lang !== currentInputLang),
        );
        const updatedPdfUrl = Object.keys(updatedTranslations).length > 0 ? updatedTranslations : undefined;
        onChange({ ...properties, pdfUrl: updatedPdfUrl });
    }, [currentInputLang, existingTranslations, onChange, properties]);

    const renderContent = () => {
        if (isActuallyLoading) {
            return (
                <FlexBox vAlign={Alignment.center}>
                    <ProgressCircular />
                </FlexBox>
            );
        }
        if (selectedFile) {
            return (
                <UploadFileControlled
                    selectedFile={selectedFile}
                    icon={mdiPlusBox}
                    label={translateKey(GLOBAL.UPLOAD_FILE)}
                    canEdit={false}
                    mode="button"
                    onDelete={onDelete}
                    linkFile={linkFile}
                    fullWidth
                    textProps={{ truncate: true }}
                />
            );
        }
        return (
            <EmptyState
                title={translateKey(WIDGET_PDF_PREVIEW.NO_PDF_SELECTED)}
                buttonProps={{
                    emphasis: Emphasis.medium,
                    children: translateKey(WIDGET_PDF_PREVIEW.SELECT_PDF),
                    onClick: () => setIsOpen(true),
                }}
                iconProps={{ icon: mdiFilePdf, color: ColorPalette.red, size: Size.m }}
            />
        );
    };

    return (
        <SettingSection title={translateKey(WIDGET_PDF_PREVIEW.SELECTED_PDF)}>
            {renderContent()}
            <PdfPickerDialog
                isMultipleSelect={false}
                isOpen={isOpen}
                onConfirm={onConfirmHandler}
                onCancel={() => setIsOpen(false)}
            />
        </SettingSection>
    );
};
